/**
 * useFlowLogger.ts
 * フロー実行時のログを管理するカスタムフック
 * パフォーマンス最適化のためバッファリングを実装
 */
import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * ログ管理フック
 *
 * 特徴：
 * - ログをバッファに一時保存し、16msごとにまとめてstateに反映
 * - 高頻度のログ出力でも再レンダリングを抑制
 * - clearLogs/flushLogsで手動制御も可能
 *
 * @returns {Object} ログ管理オブジェクト
 * @returns logs - 現在のログ配列
 * @returns log - ログを追加する関数
 * @returns clearLogs - ログをクリアする関数
 * @returns flushLogs - バッファを強制的にフラッシュする関数
 */
export function useFlowLogger() {
  // 表示用のログ配列（React state）
  const [logs, setLogs] = useState<string[]>([]);

  // バッファ（頻繁な更新を一時保存）
  const logBufferRef = useRef<string[]>([]);

  // フラッシュ用タイマーのID
  const flushTimeoutRef = useRef<number | null>(null);

  // コンポーネントのアンマウント時にタイマーをクリーンアップ
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current !== null) {
        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }
    };
  }, []);

  /** バッファの内容をstateに反映（フラッシュ） */
  const flushLogs = useCallback(() => {
    if (logBufferRef.current.length > 0) {
      const buffered = logBufferRef.current;
      logBufferRef.current = [];
      setLogs((prev) => [...prev, ...buffered]);
    }
    flushTimeoutRef.current = null;
  }, []);

  /**
   * ログを追加
   * 直接stateを更新せず、バッファに追加して16ms後にフラッシュ
   */
  const log = useCallback((line: string) => {
    logBufferRef.current.push(line);
    // タイマーが未設定ならフラッシュをスケジュール
    if (flushTimeoutRef.current === null) {
      flushTimeoutRef.current = window.setTimeout(flushLogs, 16);
    }
  }, [flushLogs]);

  /** ログとバッファを全てクリア */
  const clearLogs = useCallback(() => {
    setLogs([]);
    logBufferRef.current = [];
    if (flushTimeoutRef.current !== null) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
  }, []);

  return { logs, log, clearLogs, flushLogs };
}

